import { useRef } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, CreditCard } from "lucide-react";
import AudioListen from "@/components/AudioListen";
import { JourneyStrip } from "@/components/JourneyStrip";
import { MODULES } from "@/constants/testIds";
import { CURRICULUM } from "@/lib/curriculum";
import { MOODS, MOOD_DOT, posterFor } from "@/lib/journey";
import { startStripeCheckout } from "@/lib/payments";
import { useAuth } from "@/context/AuthContext";

const GUMROAD_BUNDLE = "https://richculm.gumroad.com/l/lkejj";

const SPANS = { m1: "md:col-span-4", m2: "md:col-span-5", m3: "md:col-span-3", m4: "md:col-span-3", m5: "md:col-span-6", m6: "md:col-span-3", m7: "md:col-span-4", m8: "md:col-span-8" };
const modules = CURRICULUM.map((m) => ({ ...m, number: m.n, url: GUMROAD_BUNDLE, span: SPANS[m.id] }));

function Card({ m, userId }) {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--x", `${e.clientX - rect.left}px`);
    el.style.setProperty("--y", `${e.clientY - rect.top}px`);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      data-testid={MODULES.card(m.id)}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, ease: [0.65, 0, 0.35, 1] }}
      className={`spotlight tacc-card group col-span-12 ${m.span} p-8 md:p-10 flex flex-col justify-between min-h-[280px] md:min-h-[340px] relative`}
    >
      <img
        src={posterFor(m.id)}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-50 group-hover:scale-[1.03] transition-all duration-700"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#FFFFFF] via-[#FFFFFF]/85 to-[#FFFFFF]/30" />
      <div className="relative z-10 flex items-start justify-between">
        <span className="font-display text-6xl md:text-7xl text-[#44403C] leading-none group-hover:text-[#B45309] transition-colors duration-700">
          {m.number}
        </span>
        <span className="flex items-center gap-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C]" data-testid={`module-mood-${m.id}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${MOOD_DOT[MOODS[m.id]]}`} />
          {MOODS[m.id]} · {m.duration}
        </span>
      </div>
      <div className="relative z-10">
        <div className="section-tag mb-3">{m.focus}</div>
        <h3 className="font-heading text-2xl md:text-3xl leading-tight tracking-tight mb-3 max-w-md">
          {m.title}
        </h3>
        {m.snippet && (
          <p className="text-[#78716C] text-sm leading-relaxed mb-4 max-w-md">
            {m.snippet}
          </p>
        )}
        <div className="mb-4">
          <AudioListen
            text={`Module ${m.number}. ${m.title}. ${m.snippet || `This session focuses on ${m.focus.toLowerCase()}, in ${m.duration}.`}`}
            label="Play narration"
            testid={`module-listen-${m.number}`}
            analyticsKind="module"
            analyticsId={m.id}
            bookmarkTitle={`Module ${m.number} — ${m.title}`}
            bookmarkUrl={m.url}
          />
        </div>
        <div className="flex flex-col items-end pt-4 border-t border-[#E7DFD5] gap-2">
          <button
            type="button"
            onClick={() => startStripeCheckout("bundle", userId)}
            data-testid={MODULES.buyBtn(m.id)}
            className="btn-ember text-xs shrink-0"
          >
            <CreditCard size={13} /> Buy with Card — $77
          </button>
          <a
            href={m.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`module-gumroad-${m.id}`}
            className="font-mono text-[9px] tracking-[0.25em] uppercase text-[#A8A29E] hover:text-[#B45309] transition-colors"
          >
            Full video of Rich on Gumroad <ArrowUpRight size={11} className="inline" />
          </a>
        </div>
      </div>
    </motion.div>
  );
}

export default function ModulesBento({ heading = true }) {
  const { user } = useAuth();
  const userId = user && user.id ? user.id : null;
  return (
    <section id="modules" className="relative bg-[#FAF7F2] py-28 md:py-40 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        {heading && (
          <div className="grid grid-cols-12 gap-y-10 mb-16">
            <div className="col-span-12 md:col-span-5">
              <div className="section-tag mb-6">§ The 8 Modules · Listen first</div>
              <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tight">
                You don't go around it. You go <span className="italic text-[#B45309]">through</span>.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-6 md:col-start-7 flex flex-col justify-end gap-6">
              <p className="text-[#44403C] text-lg md:text-xl leading-relaxed max-w-xl">
                Eight guided modules — preview each in Rich&apos;s narrated voice, then unlock all 8 with one secure card purchase.
              </p>
              <div className="flex items-center gap-4" data-testid="modules-guide-chip">
                <span className="relative block w-14 h-14 shrink-0">
                  <span className="absolute inset-0 rounded-full border border-[#F59E0B]/40" />
                  <img src="/author/avatar.jpg" alt="Rich Culmer" className="w-14 h-14 rounded-full object-cover border-2 border-[#F59E0B]/70" />
                </span>
                <div>
                  <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#B45309]">Your guide</div>
                  <div className="font-display text-xl">Rich Culmer — in his own voice.</div>
                </div>
              </div>
            </div>
          </div>
        )}

        <JourneyStrip className="mb-14 md:mb-20" />

        <div data-testid={MODULES.grid} className="grid grid-cols-12 gap-4 md:gap-6">
          {modules.map((m) => (
            <Card key={m.id} m={m} userId={userId} />
          ))}
        </div>
      </div>
    </section>
  );
}
